import { useNavigate } from 'react-router-dom';
import type {
  RunReport,
  SupplierPriceList,
  SupplierPriceListDetail,
} from '@/api/supplier-price-lists/supplier-price-lists.types';
import { DsAlert, DsButton, DsKv } from '@/modules/internal-v2/ds';
import { ContainedPage } from '../../shared/contained-page.component';
import {
  fmtCount,
  fmtDateTime,
  missingOffers,
  missingOffersText,
} from './supplier-feed-missing-offers';

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface Props {
  feed: SupplierPriceList;
}

export function SupplierFeedOverview({ feed }: { feed: SupplierPriceListDetail }) {
  return (
    <ContainedPage contained>
      {feed.incompleteRun && (
        <IncompleteRunHint feedId={feed.id} run={feed.incompleteRun} />
      )}
      <div className="admin-card-grid">
        <GeneralCard feed={feed} />
        <TransportCard feed={feed} />
        <FormatCard feed={feed} />
        <PricingCard feed={feed} />
      </div>
      <FieldMappingCard feed={feed} />
      {feed.rowFilters && feed.rowFilters.length > 0 && (
        <RowFiltersCard feed={feed} />
      )}
    </ContainedPage>
  );
}

/**
 * Shown while the newest processed run looked incomplete and nobody decided
 * about the offers it did not contain (EOS-184).
 */
function IncompleteRunHint({ feedId, run }: { feedId: string; run: RunReport }) {
  const navigate = useNavigate();
  const missing = missingOffers(run);
  return (
    <DsAlert severity="warn" className="spl-incomplete-hint">
      <span>
        Der letzte verarbeitete Lauf ({fmtDateTime(run.startedAt)}) hat nur{' '}
        {fmtCount(run.rowsSeen ?? 0)} von {fmtCount(run.rowsActiveBefore ?? 0)} Angeboten
        gesehen – möglicherweise unvollständig. {missingOffersText(missing)}{' '}
        {missing === 1 ? 'wurde' : 'wurden'} nicht ausgeblendet und{' '}
        {missing === 1 ? 'zählt' : 'zählen'} im Preis-Tool weiter, bis jemand entscheidet.
      </span>
      <DsButton
        size="small"
        onClick={() => navigate(`/i/admin/supplier-price-lists/${feedId}/runs`)}
      >
        Zu den Läufen
      </DsButton>
    </DsAlert>
  );
}

function GeneralCard({ feed }: Props) {
  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <span className="admin-card-title">Allgemein</span>
      </div>
      <DsKv
        keyWidth={120}
        entries={[
          { label: 'Name', value: feed.name },
          {
            label: 'Lieferant BP',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{feed.supplierBpId}</span>
            ),
          },
          {
            label: 'Status',
            value: feed.enabled ? (
              <span className="ds-pill pos">Aktiv</span>
            ) : (
              <span className="ds-pill muted">Deaktiviert</span>
            ),
          },
          {
            label: 'Zeitplan',
            value: feed.scheduleCron ? (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{feed.scheduleCron}</span>
            ) : (
              'Nur manuell'
            ),
          },
          {
            label: 'Nächster Lauf',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(feed.nextRunAt)}</span>
            ),
          },
          {
            label: 'Letzter Lauf',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(feed.lastRunAt)}</span>
            ),
          },
        ]}
      />
    </div>
  );
}

function TransportCard({ feed }: Props) {
  const t = feed.transport;
  let entries: { label: string; value: React.ReactNode }[];
  if (t.kind === 'http') {
    entries = [
      { label: 'Typ', value: 'HTTP' },
      {
        label: 'URL',
        value: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{t.url}</span>,
      },
      { label: 'Methode', value: t.method ?? 'GET' },
      {
        label: 'Auth',
        value: t.auth?.kind === 'basic'
          ? `Basic (${t.auth.user})`
          : t.auth?.kind === 'bearer'
            ? 'Bearer Token'
            : 'Keine',
      },
    ];
  } else if (t.kind === 'sftp') {
    entries = [
      { label: 'Typ', value: 'SFTP' },
      {
        label: 'Host',
        value: (
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            {t.host}{t.port ? `:${t.port}` : ''}
          </span>
        ),
      },
      { label: 'Benutzer', value: t.user },
      {
        label: 'Pfad',
        value: <span style={{ fontFamily: 'var(--font-mono)' }}>{t.path}</span>,
      },
    ];
  } else {
    entries = [
      { label: 'Typ', value: t.secure ? 'FTPS' : 'FTP' },
      {
        label: 'Host',
        value: (
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            {t.host}{t.port ? `:${t.port}` : ''}
          </span>
        ),
      },
      { label: 'Benutzer', value: t.user },
      {
        label: 'Pfad',
        value: <span style={{ fontFamily: 'var(--font-mono)' }}>{t.path}</span>,
      },
    ];
  }
  if (t.archive) {
    entries.push({ label: 'Archiv', value: 'ZIP (erste Datei wird entpackt)' });
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <span className="admin-card-title">Transport</span>
        <span className="admin-card-badge">{t.kind.toUpperCase()}</span>
      </div>
      <DsKv keyWidth={120} entries={entries} />
    </div>
  );
}

function FormatCard({ feed }: Props) {
  const f = feed.format;
  const entries =
    f.kind === 'csv'
      ? [
          { label: 'Format', value: 'CSV' },
          {
            label: 'Trennzeichen',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{f.delimiter ?? ','}</span>
            ),
          },
          { label: 'Encoding', value: f.encoding ?? 'utf-8' },
          { label: 'Header', value: f.hasHeader !== false ? 'Ja' : 'Nein' },
          ...(f.quoteChar === false ? [{ label: 'Quoting', value: 'Deaktiviert' }] : []),
        ]
      : f.kind === 'json'
        ? [
            { label: 'Format', value: 'JSON' },
            {
              label: 'Root Path',
              value: f.rootJsonPath ? (
                <span style={{ fontFamily: 'var(--font-mono)' }}>{f.rootJsonPath}</span>
              ) : null,
              missing: !f.rootJsonPath,
            },
          ]
        : [
            { label: 'Format', value: 'XML' },
            {
              label: 'Record Tag',
              value: f.recordTag ? (
                <span style={{ fontFamily: 'var(--font-mono)' }}>{f.recordTag}</span>
              ) : null,
              missing: !f.recordTag,
            },
          ];

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <span className="admin-card-title">Format</span>
        <span className="admin-card-badge">{f.kind.toUpperCase()}</span>
      </div>
      <DsKv keyWidth={120} entries={entries} />
    </div>
  );
}

function PricingCard({ feed }: Props) {
  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <span className="admin-card-title">Preise</span>
      </div>
      <DsKv
        keyWidth={120}
        entries={[
          {
            label: 'Währung',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{feed.currencyDefault}</span>
            ),
          },
          {
            label: 'Preiseinheit',
            value: (
              <span style={{ fontFamily: 'var(--font-mono)' }}>{feed.priceUnit}</span>
            ),
          },
          { label: 'Inkl. MwSt.', value: feed.priceIncludesVat ? 'Ja' : 'Nein' },
        ]}
      />
    </div>
  );
}

function FieldMappingCard({ feed }: Props) {
  return (
    <div className="spl-field-mapping">
      <div className="admin-card-header" style={{ marginBottom: 8 }}>
        <span className="admin-card-title">Feldzuordnung</span>
      </div>
      <pre
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--ink-2)',
          background: 'var(--surface-2)',
          padding: '10px 12px',
          borderRadius: 2,
          border: '1px solid var(--border)',
          whiteSpace: 'pre-wrap',
          margin: 0,
          lineHeight: 1.6,
        }}
      >
        {JSON.stringify(feed.fieldMapping, null, 2)}
      </pre>
    </div>
  );
}

function RowFiltersCard({ feed }: Props) {
  const filters = feed.rowFilters ?? [];
  return (
    <div className="spl-field-mapping">
      <div className="admin-card-header" style={{ marginBottom: 8 }}>
        <span className="admin-card-title">Zeilenfilter</span>
        <span className="admin-card-badge">{filters.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filters.map((f, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
            <span style={{ color: 'var(--ink-3)', textTransform: 'uppercase', fontWeight: 600, width: 60 }}>{f.mode}</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>Spalte {f.field}</span>
            <span style={{ color: 'var(--ink-3)' }}>≈</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{f.pattern}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
